import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch, getAdminUser } from '../../utils/auth';
import { getBackendUrl } from '../../utils/api';
import { formatIST, formatISTDate } from '../../utils/dateFormat';

// Ant Design imports
import {
  Card,
  Typography,
  Button,
  Alert,
  Space,
  Input,
  Tag,
  Avatar,
  Select,
  Row,
  Col,
  Drawer,
  Divider,
  Collapse,
  Tooltip,
  Skeleton,
  Modal,
  Form,
  message,
  Table,
  Flex,
} from 'antd';
import type { TableColumnsType } from 'antd';

// Ant Design Icons
import {
  ReloadOutlined,
  CloseOutlined,
  UserOutlined,
  MailOutlined,
  SafetyOutlined,
  SettingOutlined,
  DownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  LockOutlined,
  CrownOutlined,
  AuditOutlined,
  BarChartOutlined,
  BankOutlined,
  VerifiedOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Password } = Input;

interface AdminUser {
  id: number;
  username: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface Permission {
  id: number;
  name: string;
  resource: string;
  action: string;
  description: string;
}

interface Role {
  role: string;
  permission_count: number;
  permissions: Permission[];
}

const ROLE_CONFIG: Record<string, { icon: React.ReactNode; color: string; description: string }> = {
  super_admin: {
    icon: <CrownOutlined />,
    color: '#7c3aed',
    description: 'Full access to everything including user management',
  },
  admin: {
    icon: <SettingOutlined />,
    color: '#2563eb',
    description: 'Manage clients and approvals, no user management',
  },
  approver: {
    icon: <AuditOutlined />,
    color: '#059669',
    description: 'Approve/reject client registrations, view client details',
  },
  analyst: {
    icon: <BarChartOutlined />,
    color: '#d97706',
    description: 'View analytics, logs, client usage, export reports',
  },
  tenant_admin: {
    icon: <BankOutlined />,
    color: '#dc2626',
    description: 'Manage users within their own client organization',
  },
};

type TabType = 'users' | 'roles';

interface UserManagementProps {
  initialTab?: TabType;
}

function UserManagement({ initialTab = 'users' }: UserManagementProps) {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Form instance
  const [form] = Form.useForm();

  // Get current user to check permissions
  const currentUser = getAdminUser();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  // Filter state
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const loadAdminUsers = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('is_active', statusFilter);

      const response = await adminFetch(`${getBackendUrl()}/api/admin/admin-users?${params}`);
      if (response.ok) {
        const data = await response.json();
        setAdminUsers(data.data?.users || []);
        setError('');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        setError(`Failed to load admin users: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err: any) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [roleFilter, statusFilter]);

  const loadRoles = useCallback(async () => {
    try {
      const response = await adminFetch(`${getBackendUrl()}/api/admin/roles`);
      if (response.ok) {
        const data = await response.json();
        setRoles(data.data || []);
      }
    } catch (err: any) {
      // Silently fail
    }
  }, []);

  const loadPermissions = useCallback(async () => {
    try {
      const response = await adminFetch(`${getBackendUrl()}/api/admin/permissions`);
      if (response.ok) {
        const data = await response.json();
        setPermissions(data.data || []);
      }
    } catch (err: any) {
      // Silently fail
    }
  }, []);

  const loadUserDetails = useCallback(async (userId: number) => {
    try {
      const response = await adminFetch(`${getBackendUrl()}/api/admin/admin-users/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUserPermissions(data.data?.permissions || []);
      }
    } catch (err: any) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    loadAdminUsers();
    loadRoles();
    loadPermissions();
  }, [loadAdminUsers, loadRoles, loadPermissions]);

  const handleUserClick = (user: AdminUser) => {
    setSelectedUser(user);
    loadUserDetails(user.id);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedUser(null);
    setUserPermissions([]);
  };

  const handleOpenCreateModal = () => {
    form.resetFields();
    form.setFieldsValue({ role: 'admin' });
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    form.resetFields();
  };

  const handleCreateUser = async () => {
    try {
      const values = await form.validateFields();
      setCreating(true);

      const response = await adminFetch(`${getBackendUrl()}/api/admin/admin-users`, {
        method: 'POST',
        body: JSON.stringify(values),
      });

      if (response.ok) {
        message.success('Admin user created successfully');
        handleCloseCreateModal();
        loadAdminUsers();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        message.error(errorData.detail || 'Failed to create user');
      }
    } catch (err: any) {
      if (err.errorFields) {
        // Validation error, form will show errors
        return;
      }
      message.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (user: AdminUser) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleting(true);

      const response = await adminFetch(`${getBackendUrl()}/api/admin/admin-users/${userToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        message.success('Admin user deleted successfully');
        setDeleteModalOpen(false);
        setUserToDelete(null);
        loadAdminUsers();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        message.error(errorData.detail || 'Failed to delete user');
      }
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleUserStatus = async (user: AdminUser) => {
    try {
      const response = await adminFetch(`${getBackendUrl()}/api/admin/admin-users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !user.is_active }),
      });

      if (response.ok) {
        message.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
        loadAdminUsers();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        message.error(errorData.detail || 'Failed to update user');
      }
    } catch (err: any) {
      message.error(err.message);
    }
  };

  const getRoleColor = (role: string): string => {
    switch (role) {
      case 'super_admin':
        return 'purple';
      case 'admin':
        return 'blue';
      case 'approver':
        return 'green';
      case 'analyst':
        return 'orange';
      case 'tenant_admin':
        return 'red';
      default:
        return 'default';
    }
  };

  // Filter data based on search
  const filteredUsers = adminUsers.filter((user) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      user.username.toLowerCase().includes(search) ||
      user.email.toLowerCase().includes(search) ||
      (user.full_name && user.full_name.toLowerCase().includes(search))
    );
  });

  // Table columns for admin users with tooltips
  const adminUserColumns: TableColumnsType<AdminUser> = [
    {
      title: (
        <Tooltip title="Unique identifier for each admin user">
          <span>ID</span>
        </Tooltip>
      ),
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: (
        <Tooltip title="Login name for the admin user">
          <span>Username</span>
        </Tooltip>
      ),
      dataIndex: 'username',
      key: 'username',
      sorter: (a, b) => a.username.localeCompare(b.username),
      render: (value: string, record: AdminUser) => (
        <Space>
          <Avatar
            size={32}
            style={{
              backgroundColor: ROLE_CONFIG[record.role]?.color || '#1677ff',
              fontSize: '0.875rem',
            }}
          >
            {value?.[0]?.toUpperCase()}
          </Avatar>
          <Button type="link" onClick={() => handleUserClick(record)} style={{ padding: 0, fontWeight: 500 }}>
            {value}
          </Button>
        </Space>
      ),
    },
    {
      title: (
        <Tooltip title="Display name of the admin user">
          <span>Full Name</span>
        </Tooltip>
      ),
      dataIndex: 'full_name',
      key: 'full_name',
      sorter: (a, b) => (a.full_name || '').localeCompare(b.full_name || ''),
      render: (value: string | null) => value || '-',
    },
    {
      title: (
        <Tooltip title="Contact email address">
          <span>Email</span>
        </Tooltip>
      ),
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: (
        <Tooltip title="Determines what actions the user can perform. See Roles & Permissions tab for details.">
          <span>Role</span>
        </Tooltip>
      ),
      dataIndex: 'role',
      key: 'role',
      width: 140,
      filters: [
        { text: 'Super Admin', value: 'super_admin' },
        { text: 'Admin', value: 'admin' },
        { text: 'Approver', value: 'approver' },
        { text: 'Analyst', value: 'analyst' },
        { text: 'Tenant Admin', value: 'tenant_admin' },
      ],
      onFilter: (value, record) => record.role === value,
      render: (value: string) => (
        <Tooltip title={ROLE_CONFIG[value]?.description}>
          <Tag color={getRoleColor(value)} style={{ textTransform: 'capitalize' }}>
            {value?.replace('_', ' ')}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: (
        <Tooltip title="Active: Can log in | Inactive: Cannot access system">
          <span>Status</span>
        </Tooltip>
      ),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
      render: (value: boolean) => (
        <Tag color={value ? 'success' : 'error'} icon={value ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {value ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: (
        <Tooltip title="When this user last logged in to the admin panel">
          <span>Last Login</span>
        </Tooltip>
      ),
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      width: 130,
      sorter: (a, b) => new Date(a.last_login_at || 0).getTime() - new Date(b.last_login_at || 0).getTime(),
      render: (value: string | null) => (value ? formatISTDate(value) : 'Never'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record: AdminUser) => (
        <Space size="small">
          <Tooltip title="View details">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => handleUserClick(record)} />
          </Tooltip>
          {isSuperAdmin && record.id !== currentUser?.id && (
            <>
              <Tooltip title={record.is_active ? 'Deactivate' : 'Activate'}>
                <Button
                  type="text"
                  size="small"
                  icon={
                    record.is_active ? (
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    ) : (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    )
                  }
                  onClick={() => handleToggleUserStatus(record)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteClick(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  if (loading && adminUsers.length === 0) {
    return (
      <div>
        <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
          <Skeleton.Input active style={{ width: 200 }} />
          <Skeleton.Button active style={{ width: 100 }} />
        </Flex>
        <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 24 }} />
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  // Render users tab content
  const renderUsersContent = () => (
    <>
      {/* Info Panel */}
      <Alert
        type="info"
        showIcon
        message="Admin User Management"
        description="Manage admin users who have access to this admin panel. Each role has different permissions."
        style={{ marginBottom: 16 }}
      />

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Space wrap>
          <Tooltip title="Filter users by their assigned role">
            <Select
              placeholder="Filter by Role"
              value={roleFilter || undefined}
              onChange={(value) => setRoleFilter(value || '')}
              allowClear
              style={{ minWidth: 150 }}
              options={[
                { label: 'All Roles', value: '' },
                { label: 'Super Admin', value: 'super_admin' },
                { label: 'Admin', value: 'admin' },
                { label: 'Approver', value: 'approver' },
                { label: 'Analyst', value: 'analyst' },
                { label: 'Tenant Admin', value: 'tenant_admin' },
              ]}
            />
          </Tooltip>
          <Tooltip title="Filter users by their account status">
            <Select
              placeholder="Filter by Status"
              value={statusFilter || undefined}
              onChange={(value) => setStatusFilter(value || '')}
              allowClear
              style={{ minWidth: 150 }}
              options={[
                { label: 'All Status', value: '' },
                { label: 'Active', value: 'true' },
                { label: 'Inactive', value: 'false' },
              ]}
            />
          </Tooltip>
          <Tooltip title="Search by username, email, or full name">
            <Input.Search
              placeholder="Search users..."
              allowClear
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />
          </Tooltip>
        </Space>
      </Card>

      {/* Admin Users Table */}
      <Card
        title={
          <Space>
            <Title level={5} style={{ margin: 0 }}>
              Admin Users
            </Title>
            <Tag color="blue">{filteredUsers.length}</Tag>
          </Space>
        }
      >
        <Table
          dataSource={filteredUsers}
          columns={adminUserColumns}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 25,
            pageSizeOptions: ['10', '25', '50'],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
          }}
          scroll={{ y: 500 }}
          size="middle"
        />
      </Card>
    </>
  );

  // Render roles tab content
  const renderRolesContent = () => (
    <Row gutter={[24, 24]}>
      {roles.map((role) => {
        const config = ROLE_CONFIG[role.role] || {};
        return (
          <Col xs={24} sm={12} md={8} key={role.role}>
            <Card
              hoverable
              style={{
                height: '100%',
                borderTop: `3px solid ${config.color || '#1677ff'}`,
              }}
            >
              <Flex gap={16} align="flex-start" style={{ marginBottom: 16 }}>
                <Avatar
                  size={48}
                  style={{ backgroundColor: config.color || '#1677ff', flexShrink: 0 }}
                  icon={config.icon}
                />
                <div>
                  <Title level={5} style={{ margin: 0, textTransform: 'capitalize' }}>
                    {role.role.replace('_', ' ')}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {config.description}
                  </Text>
                </div>
              </Flex>

              <Tag color="blue" style={{ marginBottom: 16 }}>
                {role.permission_count} permissions
              </Tag>

              <Collapse
                ghost
                items={[
                  {
                    key: '1',
                    label: (
                      <Text type="secondary" strong>
                        View Permissions
                      </Text>
                    ),
                    children: (
                      <div style={{ maxHeight: 250, overflow: 'auto' }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {role.permissions?.map((perm: Permission) => (
                            <Card key={perm.id} size="small" style={{ backgroundColor: '#fafafa' }}>
                              <Text strong style={{ fontSize: 13 }}>
                                {perm.name}
                              </Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {perm.description}
                              </Text>
                            </Card>
                          ))}
                        </Space>
                      </div>
                    ),
                  },
                ]}
              />
            </Card>
          </Col>
        );
      })}
    </Row>
  );

  // Render content based on initialTab
  const renderContent = () => {
    switch (initialTab) {
      case 'users':
        return renderUsersContent();
      case 'roles':
        return renderRolesContent();
      default:
        return renderUsersContent();
    }
  };

  return (
    <div>
      {/* Header */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          User Management
        </Title>
        <Space>
          {isSuperAdmin && (
            <Tooltip title="Create a new administrator account with specific role and permissions">
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateModal}>
                Add Admin User
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Refresh the user list to see the latest data">
            <Button icon={<ReloadOutlined />} onClick={loadAdminUsers} loading={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </Tooltip>
        </Space>
      </Flex>

      {/* Error */}
      {error && (
        <Alert message={error} type="error" closable onClose={() => setError('')} style={{ marginBottom: 24 }} showIcon />
      )}

      {/* Content */}
      {renderContent()}

      {/* User Details Drawer */}
      <Drawer
        title={
          <Flex align="center" gap={12}>
            <Avatar
              size={48}
              style={{
                backgroundColor: selectedUser ? ROLE_CONFIG[selectedUser.role]?.color || '#1677ff' : '#1677ff',
                fontSize: '1.25rem',
              }}
            >
              {selectedUser?.username?.[0]?.toUpperCase()}
            </Avatar>
            <div>
              <Text strong style={{ fontSize: 16 }}>
                {selectedUser?.username}
              </Text>
              <br />
              <Text type="secondary">{selectedUser?.full_name || 'No name set'}</Text>
            </div>
          </Flex>
        }
        placement="right"
        width={450}
        open={drawerOpen}
        onClose={handleCloseDrawer}
        extra={
          <Button type="text" icon={<CloseOutlined />} onClick={handleCloseDrawer} />
        }
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Email */}
          <div>
            <Flex align="center" gap={8} style={{ marginBottom: 4 }}>
              <MailOutlined style={{ color: '#8c8c8c' }} />
              <Text type="secondary">Email</Text>
            </Flex>
            <Text>{selectedUser?.email}</Text>
          </div>

          {/* Role */}
          <div>
            <Flex align="center" gap={8} style={{ marginBottom: 4 }}>
              <SettingOutlined style={{ color: '#8c8c8c' }} />
              <Text type="secondary">Role</Text>
            </Flex>
            <Tag color={getRoleColor(selectedUser?.role || '')} style={{ textTransform: 'capitalize' }}>
              {selectedUser?.role?.replace('_', ' ')}
            </Tag>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {selectedUser && ROLE_CONFIG[selectedUser.role]?.description}
            </Text>
          </div>

          {/* Status */}
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Status
            </Text>
            <Tag
              color={selectedUser?.is_active ? 'success' : 'error'}
              icon={selectedUser?.is_active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            >
              {selectedUser?.is_active ? 'Active' : 'Inactive'}
            </Tag>
          </div>

          {/* Last Login */}
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Last Login
            </Text>
            <Text>{selectedUser?.last_login_at ? formatIST(selectedUser.last_login_at) : 'Never'}</Text>
          </div>

          {/* Created */}
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Created
            </Text>
            <Text>{selectedUser?.created_at ? formatIST(selectedUser.created_at) : 'N/A'}</Text>
          </div>

          <Divider />

          {/* Permissions */}
          <div>
            <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
              <VerifiedOutlined style={{ color: '#8c8c8c' }} />
              <Text strong>Permissions ({userPermissions.length})</Text>
            </Flex>
            {userPermissions.length === 0 ? (
              <Alert message="Loading permissions..." type="info" style={{ padding: '8px 12px' }} />
            ) : (
              <div style={{ maxHeight: 300, overflow: 'auto' }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {userPermissions.map((perm) => (
                    <Card key={perm.id} size="small" style={{ backgroundColor: '#fafafa' }}>
                      <Text strong style={{ fontSize: 13 }}>
                        {perm.name}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {perm.description}
                      </Text>
                    </Card>
                  ))}
                </Space>
              </div>
            )}
          </div>
        </Space>
      </Drawer>

      {/* Create Admin User Modal */}
      <Modal
        title="Create Admin User"
        open={createModalOpen}
        onCancel={handleCloseCreateModal}
        footer={[
          <Button key="cancel" onClick={handleCloseCreateModal}>
            Cancel
          </Button>,
          <Button key="create" type="primary" loading={creating} onClick={handleCreateUser}>
            Create User
          </Button>,
        ]}
        width={500}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="username"
            label="Username"
            tooltip="Unique login name - use letters, numbers, and underscores"
            rules={[
              { required: true, message: 'Username is required' },
              { min: 3, message: 'Username must be at least 3 characters' },
            ]}
          >
            <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter username" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            tooltip="Used for password recovery and notifications"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Invalid email format' },
            ]}
          >
            <Input prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} placeholder="Enter email" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            tooltip="Choose a strong password with at least 8 characters"
            rules={[
              { required: true, message: 'Password is required' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
            extra="Minimum 8 characters"
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Enter password"
              iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Form.Item
            name="full_name"
            label="Full Name"
            tooltip="Display name shown in the admin panel (optional)"
          >
            <Input placeholder="Enter full name (optional)" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            tooltip="Determines what actions this user can perform"
            rules={[{ required: true, message: 'Role is required' }]}
          >
            <Select placeholder="Select role">
              <Select.Option value="super_admin">
                <Space>
                  <CrownOutlined style={{ color: ROLE_CONFIG.super_admin.color }} />
                  Super Admin
                </Space>
              </Select.Option>
              <Select.Option value="admin">
                <Space>
                  <SettingOutlined style={{ color: ROLE_CONFIG.admin.color }} />
                  Admin
                </Space>
              </Select.Option>
              <Select.Option value="approver">
                <Space>
                  <AuditOutlined style={{ color: ROLE_CONFIG.approver.color }} />
                  Approver
                </Space>
              </Select.Option>
              <Select.Option value="analyst">
                <Space>
                  <BarChartOutlined style={{ color: ROLE_CONFIG.analyst.color }} />
                  Analyst
                </Space>
              </Select.Option>
              <Select.Option value="tenant_admin">
                <Space>
                  <BankOutlined style={{ color: ROLE_CONFIG.tenant_admin.color }} />
                  Tenant Admin
                </Space>
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.role !== curr.role}>
            {({ getFieldValue }) => {
              const role = getFieldValue('role');
              return role ? (
                <Alert
                  message={ROLE_CONFIG[role]?.description}
                  type="info"
                  showIcon
                  style={{ marginBottom: 0 }}
                />
              ) : null;
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Admin User"
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="delete" type="primary" danger loading={deleting} onClick={handleDeleteUser}>
            Delete
          </Button>,
        ]}
      >
        <Text>
          Are you sure you want to delete <Text strong>{userToDelete?.username}</Text>? This action cannot be undone.
        </Text>
      </Modal>
    </div>
  );
}

export default UserManagement;
